import { globalTypes } from '@elab/core/store/types';

import { reduceAction } from '../helpers';

import types from './types';

const availableFilters = [
    { name: 'LAST_ADDED', label: 'Last Added' },
    { name: 'LOWEST_PRICE', label: 'Lowest Price' },
    { name: 'HIGHEST_PRICE', label: 'Highest Price' },
];

const defaultFilter = availableFilters[0];

export const initialState = () => ({
    activeFilter: defaultFilter,
    availableFilters,
    totalCost: 0,
    newWishlistName: null,
    isNewListNameValid: false,
    newWishlistAnalystics: null,
    errorWhileAddToWishlist: false,
    isErrorStateForSharedWishlist: false,
});

const updateWishlist = (productCode, quantity, oldData) => {
    return {
        data: {
            ...oldData,
            entries: oldData.entries.map(entry => {
                if (entry.product.code === productCode) {
                    return {
                        ...entry,
                        wants: quantity,
                    };
                }
                return entry;
            }),
        },
    };
};

const updateSharedWishlist = (productCode, quantity, oldData) => {
    return {
        sharedData: {
            ...oldData,
            entries: oldData.entries.map(entry => {
                if (entry.product.code === productCode) {
                    return {
                        ...entry,
                        wants: quantity,
                    };
                }
                return entry;
            }),
        },
    };
};

const getFullyAddedProducts = wishlistData => {
    if (!wishlistData || !wishlistData.entries.length) {
        return [];
    }

    const productsWithVariants = [];
    const fullyAddedProducts = [];

    wishlistData.entries.forEach(entry => {
        if (entry.product.variantMatrix && entry.product.variantMatrix.length) {
            productsWithVariants.push(entry.product);
        }
    });

    productsWithVariants.forEach(product => {
        if (!fullyAddedProducts.includes(product.baseProduct)) {
            const isFullyAdded = product.variantMatrix.every(variant =>
                productsWithVariants.find(product => product.code === variant.variantOption.code),
            );
            if (isFullyAdded) {
                fullyAddedProducts.push(product.baseProduct);
            }
        }
    });
    return fullyAddedProducts;
};
const totalCostCalculation = (sum, entry) => {
    let productPrice = entry.product.price ? entry.product.price.value : 0;
    if (entry.product.otherPrices && entry.product.otherPrices.length) {
        const memberPrice = entry.product.otherPrices.find(price => price.priceSource === 'MEMBER');
        productPrice = memberPrice ? memberPrice.value : productPrice;
    }

    return entry.wants ? sum + productPrice * entry.wants : sum + productPrice;
};

const getTotalCost = (wishlistData, calculateTotalWithMemberPrice) => {
    if (calculateTotalWithMemberPrice) {
        return wishlistData.entries.reduce(totalCostCalculation, 0);
    }

    return wishlistData.entries.reduce(
        (sum, entry) => sum + (entry.product.price ? entry.product.price.value : 0),
        0,
    );
};

const getCurrency = wishlistData =>
    wishlistData.entries.length && wishlistData.entries[0].product.price
        ? wishlistData.entries[0].product.price.currencyIso
        : '';

const reducers = (state = initialState(), action) => {
    switch (action.type) {
        case globalTypes.INITIALIZE:
        case types.SET_INITIAL:
            return initialState();
        case types.SET_LOADING:
            return reduceAction(state, 'loading', !!action.payload);
        case types.SET_WISHLIST: {
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.payload,
                },
                totalCost: getTotalCost(action.payload, state.calculateTotalWithMemberPrice),
                currency: getCurrency(action.payload),
                fullyAdded: getFullyAddedProducts(action.payload),
            };
        }
        case types.SET_SHARED_WISHLIST:
            return {
                ...state,
                sharedData: {
                    ...state.data,
                    ...action.payload,
                    totalCost: getTotalCost(action.payload, state.calculateTotalWithMemberPrice),
                    currency: getCurrency(action.payload),
                },
            };
        case types.SET_NEW_WISHLIST_DATA:
            return {
                ...state,
                newWishlistName: action.payload.name,
                isNewListNameValid: action.payload.valid,
            };
        case types.RESET_NEW_WISHLIST_DATA:
            return {
                ...state,
                newWishlistName: null,
                isNewListNameValid: false,
            };
        case types.LOAD_WISHLIST:
            return { ...state, data: { ...state.data } };
        case types.SET_FILTER:
            return {
                ...state,
                activeFilter: action.payload,
            };
        case types.UPDATE_PERSONAL_WISHLIST: {
            const updatedWishlist = updateWishlist(
                action.payload.productCode,
                action.payload.quantity,
                state.data,
            );
            return {
                ...state,
                ...updatedWishlist,
                totalCost: getTotalCost(updatedWishlist.data, state.calculateTotalWithMemberPrice),
            };
        }
        case types.UPDATE_SHARED_WISHLIST: {
            const updatedWishlist = updateSharedWishlist(
                action.payload.productCode,
                action.payload.quantity,
                state.sharedData,
            );
            return {
                ...state,
                ...updatedWishlist,
                totalCost: getTotalCost(
                    updatedWishlist.sharedData,
                    state.calculateTotalWithMemberPrice,
                ),
            };
        }
        case types.REMOVE_FROM_WISHLIST: {
            const updatedWishlistData = {
                ...state.data,
                entries: state.data.entries.filter(item => item.product.code !== action.payload),
            };
            return {
                ...state,
                data: {
                    ...updatedWishlistData,
                },
                totalCost: getTotalCost(updatedWishlistData, state.calculateTotalWithMemberPrice),
                fullyAdded: getFullyAddedProducts(updatedWishlistData),
            };
        }

        case types.SET_PENDING_PRODUCT:
            return reduceAction(state, 'pendingProduct', action.payload);
        case types.CALCULATE_TOTAL_WITH_MEMBER_PRICE:
            return reduceAction(state, 'calculateTotalWithMemberPrice', true);

        case types.SET_ERROR_WHILE_ADD_TO_WISHLIST:
            return {
                ...state,
                errorWhileAddToWishlist: action.payload,
            };

        case types.SET_NEW_WISHLIST_ANALYSTICS_DATA:
            return {
                ...state,
                newWishlistAnalystics: action.payload,
            };

        case types.SET_ERROR_STATE_FOR_SHARED_WISHLIST:
            return reduceAction(state, 'isErrorStateForSharedWishlist', action.payload);

        case types.SET_LIGHT_WISHLIST: {
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.payload,
                },
            };
        }

        default:
            return state;
    }
};

export default reducers;
